/** Password rules, shared so the client can say the same thing the server
 *  enforces rather than guessing at it. */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 200;
export const PASSWORD_RULE = `At least ${PASSWORD_MIN_LENGTH} characters.`;
/* — board stages —
 *
 * The admin's list (IDEA.md §8). Everybody signed in reads it — the board is
 * drawn from it, subcontractors' included — and the office changes it.
 *
 * Every write answers with the whole list, in board order, rather than the one
 * stage that was sent: making one stage the default takes it off another, and a
 * reorder moves them all, so the one stage alone would leave the board holding
 * a second default or a stale order. */
/** A stage name is a column header, and a column is narrow. */
export const STAGE_NAME_MAX = 40;
/** The most a WIP limit can be. A limit past this is not a limit. */
export const STAGE_WIP_MAX = 999;
/* — colours —
 *
 * Reference data (IDEA.md §8). Everybody signed in reads the list — every job
 * card draws a chip from it — and the office changes it. Every write answers
 * with the whole list, the way the stages do: the list is short, it is ordered
 * by how much the shop uses each colour, and a code corrected on one row is a
 * code no other row may have. */
export const COLOUR_BRAND_MAX = 60;
export const COLOUR_CODE_MAX = 40;
export const COLOUR_NAME_MAX = 80;
/* — paged lists —
 *
 * Two lists are paged and sorted on the server — the jobs and the customers —
 * and they page the same way: a 1-based page and a size go up, the rows and
 * the counts the pager reads come back, and a page past the end is clamped
 * rather than answered with nothing. What differs is only what can be ordered
 * and what can be filtered, so those live with each list below. */
/** The page sizes a list offers the office.
 *
 *  Deliberately short of either list's maximum: this is what the office picks
 *  from, not what the endpoint will take. A short page read on an iPad beats a
 *  long one scrolled past, so these stay small — the way to a particular job or
 *  customer is the search box, not a hundred rows and a pair of eyes. */
export const PAGE_SIZES = [10, 15, 25];
/** How many customers a page holds by default. */
export const CUSTOMERS_PAGE_SIZE = 25;
/** The most rows one request will answer with. A pager that could ask for
 *  everything is not a pager. */
export const CUSTOMERS_PAGE_MAX = 500;
/** What the app asks for when it wants the whole customer list rather than a
 *  page of it: the new-job form's picker, which searches every customer the
 *  shop has, and the duplicate check, which has to compare against all of them
 *  or it would wave through the customer on page two.
 *
 *  The same shape as `BOARD_PAGE_SIZE` — one page big enough to be all of it —
 *  and the same warning: a shop with more customers than this needs the picker
 *  to search on the server, not a bigger number here. */
export const CUSTOMERS_ALL_SIZE = CUSTOMERS_PAGE_MAX;
/* — jobs — */
/** How many rows the jobs list asks for by default. A page the office can read
 *  without scrolling the header off an iPad. */
export const JOBS_PAGE_SIZE = 25;
/** The most rows one request will answer with. A pager that could ask for
 *  everything is not a pager. */
export const JOBS_PAGE_MAX = 500;
/** What the board asks for.
 *
 *  A kanban cannot be paged — it is one screen of the whole shop, and a column
 *  missing its last three cards is a board nobody can trust. So the board takes
 *  one page big enough to be all of it. If a shop ever has more than this many
 *  jobs on the board at once, the board is the thing to fix, not this number. */
export const BOARD_PAGE_SIZE = JOBS_PAGE_MAX;
/* — the notice board —
 *
 * The office writes; everybody reads. There is no per-person notice and no
 * addressing: one shop, one board, which is what the whiteboard in the corner
 * was. Reading is the whole shop, subcontractors included — a notice saying
 * the roller door is broken is for whoever walks through it. */
/** A title is what the dashboard draws, so it has to fit on a line. */
export const NOTICE_TITLE_MAX = 120;
/** The detail under it. Longer than a title, shorter than a job sheet — a
 *  notice nobody can read at a glance has stopped being a notice. */
export const NOTICE_BODY_MAX = 2000;
/** How many notices the board keeps on screen. The board is what is up now,
 *  not an archive: past this the oldest simply stop being drawn, and the way
 *  to tidy the board is to take a notice down. */
export const NOTICE_BOARD_SIZE = 30;
/* — the dashboard — */
/** How many days ahead the promised-work chart runs. A fortnight is what the
 *  shop plans in, and it is the schedule screen's own longest range. */
export const DASHBOARD_DAYS = 14;
/** How many weeks of goods in the intake chart looks back over. Two months is
 *  long enough to show a trend and short enough to still be this shop. */
export const DASHBOARD_WEEKS = 8;
/** How many of my own jobs the dashboard lists before it stops and says how
 *  many there are all told. A bench is a glance, not the jobs list — and the
 *  jobs list is one press away, filtered to the same thing. */
export const DASHBOARD_MINE = 12;
/** How many rows the "biggest first" lists keep — colours on the racks, and
 *  who the shop is working for. Past six, a list stops being a glance. */
export const DASHBOARD_TOP = 6;
//# sourceMappingURL=api.js.map